import { useMutation, useQueryClient } from '@tanstack/react-query';
import EventChecklistService, {
  IUpdateEventChecklistParams,
} from '@clickersphere/core/services/EventChecklistService';
import { useBuildQueryKey } from '../useBuildQueryKey';
import {
  EventChecklistQueryData,
  EVENT_CHECKLISTS_QUERY_KEY,
} from './useEventChecklists';

interface IUpdateEventChecklistVariables {
  id: string;
  data: IUpdateEventChecklistParams;
}

export function useUpdateEventChecklist() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const {
    isPending: updateEventChecklistIsPending,
    mutateAsync: updateEventChecklistAsync,
  } = useMutation({
    mutationFn: async ({ id, data }: IUpdateEventChecklistVariables) =>
      EventChecklistService.update(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(EVENT_CHECKLISTS_QUERY_KEY),
      });

      const oldChecklists = queryClient.getQueryData<EventChecklistQueryData[]>(
        buildQueryKey(EVENT_CHECKLISTS_QUERY_KEY),
      );

      queryClient.setQueryData<EventChecklistQueryData[]>(
        buildQueryKey(EVENT_CHECKLISTS_QUERY_KEY),
        (old) =>
          old?.map((checklist) =>
            checklist.id === id
              ? { ...checklist, ...data, queryStatus: 'pending' }
              : checklist,
          ),
      );

      return { oldChecklists };
    },
    onSuccess: async (_data, { id }) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(EVENT_CHECKLISTS_QUERY_KEY),
      });

      queryClient.setQueryData<EventChecklistQueryData[]>(
        buildQueryKey(EVENT_CHECKLISTS_QUERY_KEY),
        (old) =>
          old?.map((checklist) =>
            checklist.id === id
              ? { ...checklist, queryStatus: undefined }
              : checklist,
          ),
      );
    },
    onError: async (_error, _variables, context) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(EVENT_CHECKLISTS_QUERY_KEY),
      });

      queryClient.setQueryData(
        buildQueryKey(EVENT_CHECKLISTS_QUERY_KEY),
        context?.oldChecklists,
      );
    },
  });

  return {
    updateEventChecklistAsync,
    updateEventChecklistIsPending,
  };
}
